﻿import * as _ from "underscore";
import { IOpenGL } from '../IOpenGL';

/**
 * Extract all data from the OpenGL. It uses getters to do so.
 * */
export class OpenGLParser {
    private static readonly _namesV1: string[] = [`webgl`, `experimental-webgl`];

    private static readonly _namesV2: string[] = [`webgl2`, `experimental-webgl2`]; // For V2

    private _webglVersion: number;

    private _gl: WebGLRenderingContext;

    private _contextName: string;

    /**
     * Constructs the parse, using the version of the OpenGL in use. By now, only admits version 1.
     * @param openGlVersion
     */
    constructor(openGlVersion) {
        if (openGlVersion == null) {
            console.error(`openGlVersion is NULL`);
        }

        switch (openGlVersion) {
            case 2: // OpenGLv2 is not using by now
            case 1:
                this._initializeV1();
                break;
            default:
                break;
        }
    }

    /**
     * Initializes the parser to use OpenGL V1. It creates a canvas to get OpenGL information and save it into _gl variable.
     * Once this is done, the canvas is removed.
     * */
    private _initializeV1(): void {
        this._webglVersion = 1;

        const canvas: HTMLCanvasElement = document.createElement(`canvas`); // Creates a canvas to get OpenGL information

        this._contextName = _.find(OpenGLParser._namesV1, name => {
            this._gl = canvas.getContext(name, { stencil: true });
            return Boolean(this._gl);
        });
    }

    /**
     * @returns Unmasked Renderer: Renderer string of the graphics driver
     * CURRENTLY UNUSED - POSSIBLY DELETE IN FUTURE VERSION
     * */
    public getUnmaskedRenderer(): string {
        // Next line is disabled because the extension name is what it is, I can't change that
        const dbgRenderInfo: WEBGL_debug_renderer_info = this._gl.getExtension(`WEBGL_debug_renderer_info`); // eslint-disable-line camelcase

        return this._gl.getParameter(dbgRenderInfo.UNMASKED_RENDERER_WEBGL);
    }

    /**
     * @returns Unmasked Vendor: Vendor string of the graphics driver
     * CURRENTLY UNUSED - POSSIBLY DELETE IN FUTURE VERSION
     * */
    public getUnmaskedVendor(): string {
        // Next line is disabled because the extension name is what it is, I can't change that
        const dbgRenderInfo: WEBGL_debug_renderer_info = this._gl.getExtension(`WEBGL_debug_renderer_info`); // eslint-disable-line camelcase

        return this._gl.getParameter(dbgRenderInfo.UNMASKED_VENDOR_WEBGL);
    }

    /**
    * @returns Shading Language Version: Version of the OpenGL shading language
     * CURRENTLY UNUSED - POSSIBLY DELETE IN FUTURE VERSION
    * */
    public getShadingLanguageVersion(): string {
        return this._gl.getParameter(this._gl.SHADING_LANGUAGE_VERSION);
    }

    /**
     * @returns WebGL version
     * CURRENTLY UNUSED - POSSIBLY DELETE IN FUTURE VERSION
     * */
    public getWebGlVersion(): number {
        return this._webglVersion;
    }

    /**
     * @returns Context Name of OpenGL
     * CURRENTLY UNUSED - POSSIBLY DELETE IN FUTURE VERSION
     * */
    public getContextName(): string {
        return this._contextName;
    }

    /**
     * @returns true if the antialising is available
     * CURRENTLY UNUSED - POSSIBLY DELETE IN FUTURE VERSION
     * */
    public isAntialiasingAvailable(): boolean {
        return this._gl.getContextAttributes().antialias;
    }

    /**
     * @returns Vertex Shader Info: The Vertex Shader is the programmable Shader stage in the rendering pipeline
     * that handles the processing of individual vertices
     * */
    public getVertexShaderInfo(): IOpenGL {
        const vertexShader: IOpenGL = {
            maxTextureSize: this._gl.getParameter(this._gl.MAX_TEXTURE_SIZE),
            maxVertexAttributes: this._gl.getParameter(this._gl.MAX_VERTEX_ATTRIBS),
            maxVertexTextureImageUnits: this._gl.getParameter(this._gl.MAX_VERTEX_TEXTURE_IMAGE_UNITS),
            maxVertexUniformVectors: this._gl.getParameter(this._gl.MAX_VERTEX_UNIFORM_VECTORS)
        };

        return vertexShader;
    }
}
